// WHERE a raw task came from and WHERE it is in the admission lifecycle: the inlet
// vocabulary, the status vocabulary, and the one derivation that maps a triage verdict
// onto the status it settles to.
// Split out of `../raw-task.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { TriageVerdict } from '../../value-objects/triage-verdict';
import type { RawTask } from './row';
import {
  RAW_TASK_DISCARDED_VERDICT,
  RAW_TASK_WITHDRAWN_VERDICT,
  type RawTaskVerdict,
} from './verdict';

/**
 * WHERE a raw task came from — the inlet, not the intent. One member per door
 * named in `docs/idea/lifecycle.md` § 1 ("The ask") and § "The spine".
 *
 * Closed and exhaustive BY CONSTRUCTION: a `const` object plus a `z.enum` over
 * its members, the `OutcomeKind` idiom (`entities/task.ts`). Adding a member
 * here is the only way to add a source, and every total `switch` over the union
 * fails to compile until it handles the new one.
 */
export const RawTaskSource = {
  /** A scout's authored plan group (`plan_authored` → `scout-plan-ingest.ts`). */
  Scout: 'scout',
  /**
   * A GitHub issue, filed on `issues.opened` by
   * `apps/worker/src/dispatch/gh-issue-intake.ts`.
   *
   * This used to read "an issue that carried the claim gesture", and the code has
   * never checked one — none of the three gestures
   * `docs/plans/2026/08/24/101-factory-of-code/16-webhook-intake.md` names for
   * GitHub is reachable today (measured on prod 2026-08-30: zero of 5,356 `issues`
   * deliveries assigned to our bot, zero mentioning it, `handoff.mode: label` on
   * zero of 51 policy rows). The inlet admits a newly OPENED, human-authored issue
   * on an active repo instead, which keeps it to work that arrives rather than a
   * backlog that already exists; the sink's header states the whole rule and where
   * a real gesture would narrow it.
   */
  GhIssue: 'gh_issue',
  /** A Sentry alert, filed by the connection that carried it. */
  Sentry: 'sentry',
  /** A Linear ticket, filed by the connection that carried it. */
  Linear: 'linear',
  /**
   * A connected webhook source with no built-in mapper (arc 101 doc 16) — a CI
   * system, an internal tool, a tracker we do not special-case. Its own member
   * rather than `api`, because `api` means a human or an agent filing work
   * directly and this is a machine we bound to a tenant by which secret verified
   * it; a triage pass reading the two as one could not tell them apart.
   */
  Webhook: 'webhook',
  /**
   * The REST/MCP write surface — a human or an agent filing work directly.
   *
   * TWO doors write it, and they are the same act by the same kind of principal:
   * `ask file` / `POST /v1/asks` (`apps/api` `deps/asks.ts`), where a caller's AI
   * files the plan it composed from a `plan_start {return_context: true}` scout
   * run; and `POST /blocker-fix/:jwt`, the signed form a human fills in. The
   * narrower `Conversation` member below is the agent-to-agent mailbox, which is
   * why the MCP `blocker answer` verb files under THAT one — there the two doors
   * onto one blocker really are a person and an agent, and telling them apart is
   * what the column is for.
   */
  Api: 'api',
  /** A janitor's finding (`apps/worker/src/janitors/`). */
  Janitor: 'janitor',
  /**
   * A repo's default branch went red (#5239): the ONE fix ask the worker's base-red sink
   * files per `(repo, check, first red sha)` (`apps/worker/src/babysit/base-red.ts`).
   */
  BaseRed: 'base_red',
  /**
   * A deploy environment stayed red past its grace (#5344): the ONE fix ask the worker's
   * `cron.deploy_red` sweep files per `(repo, environment, first failed sha)`
   * (`apps/worker/src/cron/deploy-red.ts`).
   */
  DeployRed: 'deploy_red',
  /**
   * A repo's own gate stayed red on its BASE across the fleet's baseline probes: the ONE fix
   * ask the worker's `cron.gate_base_red` sweep files per failure set
   * (`apps/worker/src/cron/gate-base-red.ts`).
   */
  GateBaseRed: 'gate_base_red',
  /** A devops run that hit a prerequisite it could not satisfy. */
  Devops: 'devops',
  /** An agent-to-agent conversation (the scout mailbox, an MCP `ask file`). */
  Conversation: 'conversation',
  /**
   * A scout survey's `handoffs` (`apps/runner/src/modes/scout/survey/finding.ts:156`,
   * `SCOUT_MAX_HANDOFFS = 10`, `SCOUT_HANDOFF_MAX_CHARS = 300`) — "ONE piece of work
   * a scout noticed that nobody asked it about", `{forPath, work, why}`, one-PR-sized
   * and anchored. The READER-only inlet: the runner will emit `raw_task_filed`
   * payloads under this source in a later slice, and the mothership detects them
   * exactly as `devops_filing-ingest.ts` does for a devops run — a sibling schema
   * and an admission gate with the same evidence floor. {@link promptRoleForRawTask}
   * returns `Triage` for it, like every other inlet.
   */
  Survey: 'survey',
} as const;
export const RawTaskSourceSchema = z.enum(RawTaskSource);
export type RawTaskSource = z.infer<typeof RawTaskSourceSchema>;

/**
 * The admission states. Five, closed, and the union is a ONE-WAY DOOR — it is
 * stored in `raw_tasks.status`, so a member added later is a migration and a
 * member removed later is unreadable rows (`awaiting_info` was that migration,
 * plan 101 slice 08).
 *
 *   `pending`   — filed, nothing has looked at it. The only unclaimed state.
 *   `triaged`   — taken up by triage. Two sub-states, told apart by `verdict`:
 *                 NULL means claimed and in flight, non-null means settled with
 *                 a `schedule` verdict (work was, or is about to be, minted).
 *   `dropped`   — decided not to happen: noise, a duplicate of live work, or
 *                 text the intake screen discarded (`discarded`, H3 #5393).
 *                 The `verdict` says which, and a `dupe-of` names the survivor.
 *   `escalated` — a human decides. Not a failure; a first-class outcome
 *                 (`docs/idea/triage.md` § "The output is real tasks, or no tasks").
 *   `awaiting_info` — triage returned `needs_info`: the ASKER owes an answer
 *                 before the ask is decidable. Settled-shaped (verdict and
 *                 `settled_at` set, no lease) but NOT terminal: the answer moves
 *                 it back to `pending` for a fresh triage, and an expiry or the
 *                 round cap moves it to `escalated` — see
 *                 {@link RAW_TASK_STATUS_TRANSITIONS}.
 *
 * WHY `triaged` DOUBLES AS THE CLAIM STATE rather than earning a fifth member:
 * the claim has to move the row OUT of `pending` or two concurrent drains both
 * see it the moment the first one's lock is released — `FOR UPDATE SKIP LOCKED`
 * excludes a peer only while the claiming transaction is open, and triage takes
 * a model call, not a transaction. So `claimPendingRawTaskBatch` writes `triaged`
 * and `settleRawTask` writes the verdict (correcting the status when the verdict
 * is not `schedule`). "Admitted" and "admitted with a decision recorded" are the
 * same state to every reader except the settler, and `verdict IS NULL` is the
 * one predicate that separates them.
 */
export const RawTaskStatus = {
  Pending: 'pending',
  Triaged: 'triaged',
  Dropped: 'dropped',
  Escalated: 'escalated',
  AwaitingInfo: 'awaiting_info',
} as const;
export const RawTaskStatusSchema = z.enum(RawTaskStatus);
export type RawTaskStatus = z.infer<typeof RawTaskStatusSchema>;

/**
 * Every status except `pending` — what a settle may write. Includes
 * `awaiting_info`, which a settle writes and an answer later re-opens.
 */
export type SettledRawTaskStatus = Exclude<RawTaskStatus, typeof RawTaskStatus.Pending>;

/**
 * The status a verdict implies. TOTAL over `RawTaskVerdict['verdict']` — every
 * `TriageVerdict` plus the screen's `discarded` (a `switch` with no default), so a
 * new verdict fails typecheck here until it is placed.
 *
 * ONE seam on purpose: status and verdict are two spellings of one decision, and
 * a settle that took them as separate arguments could write `dropped` beside a
 * `schedule` verdict. `settleRawTask` derives the status from the verdict
 * through this function, so the disagreement is unrepresentable rather than
 * guarded.
 */
export function rawTaskStatusForVerdict(verdict: RawTaskVerdict['verdict']): SettledRawTaskStatus {
  switch (verdict) {
    case TriageVerdict.Schedule:
      return RawTaskStatus.Triaged;
    case TriageVerdict.Escalate:
      return RawTaskStatus.Escalated;
    case TriageVerdict.DropNoise:
    case TriageVerdict.DupeOf:
      return RawTaskStatus.Dropped;
    case TriageVerdict.NeedsInfo:
      return RawTaskStatus.AwaitingInfo;
    // The intake screen refused the ask's text (H3 #5393): terminal, like drop-noise.
    case RAW_TASK_DISCARDED_VERDICT:
      return RawTaskStatus.Dropped;
    // The fact that filed the ask stopped holding before triage claimed it (#5713).
    case RAW_TASK_WITHDRAWN_VERDICT:
      return RawTaskStatus.Dropped;
  }
}

/**
 * Every status move the lifecycle has, by STATUS. TOTAL over `RawTaskStatus`
 * (a `Record`), so a sixth status fails to compile here until its exits are
 * stated.
 *
 * | from | to | the act |
 * |---|---|---|
 * | `pending` | `triaged` | the claim (`claimPendingRawTaskBatch`) |
 * | `pending` | `dropped` | the withdraw of an unclaimed ask whose fact cleared (`withdrawPendingRawTask`, #5713) |
 * | `triaged` | `triaged` | an in-flight row settles `schedule`, or a lapsed lease is re-claimed |
 * | `triaged` | `dropped` / `escalated` | the settle |
 * | `triaged` | `awaiting_info` | the settle, on a `needs_info` verdict |
 * | `triaged` | `pending` | a hold (tenant or model) releases the claim |
 * | `awaiting_info` | `pending` | the asker answered — verdict, `settled_at` and `attempts` cleared in the same update |
 * | `awaiting_info` | `escalated` | nobody answered (`info_unanswered`), or the round cap bit (`info_rounds_exhausted`) |
 * | `escalated` | `pending` | ONLY a re-drive of a MACHINE-failure escalation (`RAW_TASK_MACHINE_FAILURE_REASONS`, `./redrive`): the automatic machine re-drive (budgeted, backed off) or the operator's — never a lifecycle move |
 * | `escalated` | `dropped` | ONLY the withdraw of a MACHINE-failure escalation whose GitHub issue closed (`withdrawEscalatedRawTask`, `issue_closed`) — the same shape fence as the re-drive, never a lifecycle move |
 *
 * `dropped` and `escalated` are terminal (`escalated` save the re-drive and the withdraw above,
 * kept out of this table because each is an act on a machine failure, not a step of the
 * lifecycle; their shared fence is `./redrive`). `awaiting_info -> triaged` is absent on
 * purpose: an answer re-enters through `pending` so the ordinary claim, its lease
 * and its attempt bound apply — a second door into triage would be a claim path
 * nothing leases. Status-level only: which SUB-state of `triaged` a move leaves
 * (in flight vs admitted) is the lifecycle invariant's business (`./row`), not
 * this table's.
 */
export const RAW_TASK_STATUS_TRANSITIONS: Readonly<
  Record<RawTaskStatus, readonly RawTaskStatus[]>
> = {
  [RawTaskStatus.Pending]: [RawTaskStatus.Triaged, RawTaskStatus.Dropped],
  [RawTaskStatus.Triaged]: [
    RawTaskStatus.Triaged,
    RawTaskStatus.Dropped,
    RawTaskStatus.Escalated,
    RawTaskStatus.AwaitingInfo,
    RawTaskStatus.Pending,
  ],
  [RawTaskStatus.Dropped]: [],
  [RawTaskStatus.Escalated]: [],
  [RawTaskStatus.AwaitingInfo]: [RawTaskStatus.Pending, RawTaskStatus.Escalated],
};

/** Is `from -> to` a move {@link RAW_TASK_STATUS_TRANSITIONS} lists? */
export function isRawTaskTransitionLegal(from: RawTaskStatus, to: RawTaskStatus): boolean {
  return RAW_TASK_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * May this ask be WITHDRAWN (#5713)? True for exactly the unclaimed shape: `pending`,
 * no verdict, no lease. A claimed ask (`triaged`, live lease) is triage's to decide, and
 * every settled row has already been decided. `withdrawPendingRawTask` (`@developerz/db`)
 * restates this predicate in its UPDATE's WHERE, so the write and the fence agree.
 */
export function isRawTaskWithdrawable(
  task: Pick<RawTask, 'status' | 'verdict' | 'leaseExpiresAt'>,
): boolean {
  return (
    task.status === RawTaskStatus.Pending && task.verdict === null && task.leaseExpiresAt === null
  );
}
